import { useMutation, useQueryClient } from '@tanstack/react-query';
import { batchUpdate, getBatchProgress } from '@/services/batch.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useBatchStore } from '@/hooks/data/batch/useBatchStore';
import { type BackendError } from '@/hooks/useFormErrorHandler';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { type AssetTag } from '@/types/asset';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useBatchEditAssets = () => {
  const tToasts = useTranslations('toasts');
  const queryClient = useQueryClient();

  const { toast } = useToast();
  const pollProgress = useBatchStore((state) => state.pollProgress);
  const markBatchErrored = useBatchStore((state) => state.markBatchErrored);

  const batchEditAssetsWithAuth = useAuthenticatedQueryFn(batchUpdate);
  const getBatchProgressWithAuth = useAuthenticatedQueryFn(getBatchProgress);

  const batchEditAssetsMutation = useMutation({
    mutationFn: batchEditAssetsWithAuth,
    onMutate: async ({ filter, payload }) => {
      await queryClient.cancelQueries({
        queryKey: [
          QueryKey.MultipleAssets,
          filter.folderId,
          filter.albumId,
          filter.assetIds,
          ...(filter.folderId ? [filter.includeSubFolderAssets] : []),
        ],
      });

      const previousAssets = queryClient.getQueryData([
        QueryKey.MultipleAssets,
        filter.folderId,
        filter.albumId,
        filter.assetIds,
        ...(filter.folderId ? [filter.includeSubFolderAssets] : []),
      ]);

      // Only attempt optimistic update if there's existing data
      if (previousAssets) {
        queryClient.setQueryData(
          [
            QueryKey.MultipleAssets,
            filter.folderId,
            filter.albumId,
            filter.assetIds,
            ...(filter.folderId ? [filter.includeSubFolderAssets] : []),
          ],
          (old: any) => {
            if (!old) {
              return old;
            }

            return {
              ...old,
              workflows: {
                ...old.workflows,
                ...(payload.workflows ?? {}),
              },
              metadata: {
                ...old.metadata,
                ...(payload.metadata ?? {}),
              },
              tags: {
                isFullIntersection: old.tags.isFullIntersection,
                // `payload.tags` is a flat array of tag names (the child is always the base for
                // writes); re-attach each tag's known parent from the previous cache so the
                // optimistic state keeps the `{ name, parent }` shape the rest of the app reads.
                // A name with no match is a newly created tag, which starts out parentless.
                tags: payload.tags
                  ? payload.tags.map(
                      (name: string): AssetTag =>
                        old.tags.tags.find((tag: AssetTag) => tag.name === name) ?? { name, parent: null },
                    )
                  : old.tags.tags,
              },
            };
          },
        );
      }

      return { previousAssets };
    },
    onSuccess: async (response, { filter }) => {
      try {
        const batchId = response.batchId;

        await pollProgress(batchId, getBatchProgressWithAuth, 'Edit assets');
        await queryClient.invalidateQueries({
          queryKey: [
            QueryKey.MultipleAssets,
            filter.folderId,
            filter.albumId,
            filter.assetIds,
            ...(filter.folderId ? [filter.includeSubFolderAssets] : []),
          ],
        });

        toast({
          title: tToasts('batchEditAssetsMutation.title'),
          description: tToasts('batchEditAssetsMutation.description'),
        });
      } catch (err) {
        errorLog(err);
      }
    },
    onError: (err: BackendError) => {
      markBatchErrored('Edit assets');
      errorLog(err);
    },
  });

  return {
    batchEditAssetsMutation,
  };
};
